import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import color from "@definitions/mui/color";
import { fontFamSecondary } from "@definitions/mui/custom";

export type MenuListProps = {
  tableOfContents: string[];
  onScroll: (_e: string) => void;
  isMobile?: boolean;
  onClose?: (_e: string) => void;
  onIsActive?: (_e: string) => void;
};

const MenuList: React.FC<MenuListProps> = ({
  tableOfContents,
  isMobile,
  onClose,
  onScroll,
  onIsActive,
}) => {
  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    containerRefs.current.forEach(() => {
      const handleClick = (e: MouseEvent) => {
        const link = (e.target as HTMLElement).closest('a.sidebar-link');

        if (link) {
          e.preventDefault();
          const url = new URL((link as HTMLAnchorElement).href);
          const hash = url.hash;

          const target = document.querySelector(hash);
          if (target) {
            onScroll(hash.substring(1));
          }
        }
      };

      window.addEventListener('click', handleClick);

      return () => window.removeEventListener('click', handleClick);
    });
  }, []);

  return (
    <Box
      alignSelf="flex-start"
      display="flex"
      flexDirection="column"
      width="100%"
      sx={{
        color: color.primary.main,
        fontWeight: 700,
        fontFamily: fontFamSecondary,
        listStyleType: "none",
        "&:hover": {
          overflow: "auto",
        },
        ...(!isMobile && {
          position: 'sticky',
          top: 'calc(70px + 40px + 64px + 32px + 40px)',
          maxHeight: 'calc(100vh - 70px - 40px - 64px - 32px - 40px)',
          overflow: 'hidden',
          gap: 2,
          width: '300px',
          marginRight: 5,
          paddingBottom: 4,
        })
      }}
    >
      {tableOfContents.map((e: string, index: number) => (
        <Box
          component="div"
          dangerouslySetInnerHTML={{ __html: e }}
          display="contents"
          key={`menu-${index}`}
          ref={(el: HTMLDivElement | null) => { containerRefs.current[index] = el; }}
          onClick={onClose ? () => onClose(e) : undefined}
          {...(isMobile && {
            sx: {
              '& > li > a': {
                display: 'inline-block',
                width: '100%',
                paddingY: 2,
                borderBottom: '1px solid #DEE3ED',
                color: onIsActive?.(e) ? color.primary.main : color.general.main,
                fontSize: '14px',
                fontWeight: 600,
              },
               '&:last-child > li > a': {
                border: 'none',
              },
            }
          })}
        />
      ))}
    </Box>
  );
};

export default MenuList;
